// NOTE(Hammad) will be able to delete this file fully after fully rolling out PROJECT_ACTIONS_DUPLICATE_PROJECT
// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Loading, Space} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useNavigationDOM, useResponsive, useToast} from '@supermove/hooks';

// App
import Drawer from '@shared/design/components/Drawer';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import DuplicateProjectClientFields from '@shared/modules/Project/components/Client/DuplicateProjectClientFields';
import ProjectClientFields from '@shared/modules/Project/components/Client/ProjectClientFields';
import ProjectForm from '@shared/modules/Project/forms/ProjectForm';
import useCopyProjectMutation from '@shared/modules/Project/hooks/useCopyProjectMutation';

const DuplicateProjectDrawerContent = ({project, handleClose}: any) => {
  const responsive = useResponsive();
  const {navigator} = useNavigationDOM();
  const copyProjectSuccessToast = useToast({
    ToastComponent: SuccessToast,
    // Default message, will get overriden by dynamicMessage
    message: 'Project created!',
    actionText: 'View Project',
    handleAction: ({projectUuid}: {projectUuid: string}) =>
      navigator.push(`/projects/${projectUuid}/view`),
  });
  const {form, submitting, handleSubmit} = useCopyProjectMutation({
    projectForm: ProjectForm.copy(project),
    onSuccess: ({project}: any) => {
      const projectUuid = _.get(project, 'uuid');
      const projectIdentifier = _.get(project, 'identifier');
      copyProjectSuccessToast.handleToast({
        actionPayload: {projectUuid},
        message: `Project ${projectIdentifier} created!`,
      });
      handleClose();
    },
    onError: (errors: any) => console.log({errors}),
  });
  const field = 'projectForm';

  return (
    <React.Fragment>
      <Drawer.Body>
        <DuplicateProjectClientFields
          index={0}
          form={form}
          field={`${field}.clientForm`}
          organization={project.organization}
          responsive={responsive}
        />
        <Space height={24} />
        <DuplicateProjectClientFields
          index={1}
          isShowingBillingClient
          form={form}
          field={`${field}.billingClientForm`}
          organization={project.organization}
          responsive={responsive}
        />
      </Drawer.Body>
      {responsive.desktop ? (
        <Drawer.Footer
          isDisabled={submitting}
          isSubmitting={submitting}
          primaryAction={handleSubmit}
          secondaryAction={handleClose}
          primaryActionText={'Duplicate'}
          secondaryActionText={'Cancel'}
        />
      ) : (
        <Drawer.FooterMobile
          isSubmitting={submitting}
          primaryAction={handleSubmit}
          primaryActionText={'Duplicate'}
        />
      )}
    </React.Fragment>
  );
};

const DuplicateProjectDrawer = ({projectUuid, isOpen, handleClose}: any) => {
  const responsive = useResponsive();
  const {data, loading} = useQuery(DuplicateProjectDrawer.query, {
    variables: {projectUuid},
    skip: !isOpen,
  });

  return (
    <Drawer.PreventPropagationContainer style={{position: 'absolute'}}>
      <Drawer
        isOpen={isOpen}
        handleClose={handleClose}
        width={Drawer.WIDTH.DEFAULT}
        shouldCloseOnClickOutside={false}
      >
        {responsive.desktop ? (
          <Drawer.Header
            headerText={'Duplicate Project'}
            handleClose={handleClose}
            isClosable={false}
          />
        ) : (
          <Drawer.HeaderMobile headerText={'Duplicate Project'} handleClose={handleClose} />
        )}
        <Loading loading={loading || !data}>
          {() => <DuplicateProjectDrawerContent project={data.project} handleClose={handleClose} />}
        </Loading>
      </Drawer>
    </Drawer.PreventPropagationContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
DuplicateProjectDrawer.query = gql`
  ${ProjectClientFields.fragment}
  ${ProjectForm.copy.fragment}
  query DuplicateProjectDrawer($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      organization {
        id
        ...ProjectClientFields
      }
      ...ProjectForm_copy
    }
  }
`;

export default DuplicateProjectDrawer;
